export interface RasterLottieOpacityStop {
  offset: number;
  value: number;
}

export interface RasterLottieLayer {
  src: string;
  clipPath?: string;
  initialOpacity: number;
  opacityStops: RasterLottieOpacityStop[];
  zIndex: number;
}

export interface RasterLottieAnimation {
  width: number;
  height: number;
  durationMs: number;
  layers: RasterLottieLayer[];
}

function numberArray(value: unknown): number[] | null {
  return Array.isArray(value) && value.every((item) => Number.isFinite(item))
    ? value.map(Number)
    : null;
}

function isStaticValue(property: any, expected: number | number[]): boolean {
  if (!property || property.a !== 0) return false;
  if (Array.isArray(expected)) {
    const actual = numberArray(property.k);
    return Boolean(actual && actual.length >= expected.length && expected.every((value, index) => actual[index] === value));
  }
  return Number(property.k) === expected;
}

function opacityStops(property: any, inFrame: number, outFrame: number): RasterLottieOpacityStop[] | null {
  if (property?.a === 0 && Number.isFinite(property.k)) {
    const value = Math.max(0, Math.min(1, Number(property.k) / 100));
    return [{ offset: 0, value }, { offset: 1, value }];
  }
  if (property?.a !== 1 || !Array.isArray(property.k) || property.k.length === 0) return null;
  const frameSpan = outFrame - inFrame;
  const stops = property.k.map((keyframe: any) => {
    const value = Array.isArray(keyframe?.s) ? Number(keyframe.s[0]) : Number(keyframe?.s);
    if (!Number.isFinite(keyframe?.t) || !Number.isFinite(value)) return null;
    return {
      offset: Math.max(0, Math.min(1, (Number(keyframe.t) - inFrame) / frameSpan)),
      value: Math.max(0, Math.min(1, value / 100)),
    };
  });
  if (stops.some((stop: RasterLottieOpacityStop | null) => stop === null)) return null;
  const normalized = stops as RasterLottieOpacityStop[];
  if (normalized[0].offset > 0) normalized.unshift({ offset: 0, value: normalized[0].value });
  if (normalized[normalized.length - 1].offset < 1) {
    normalized.push({ offset: 1, value: normalized[normalized.length - 1].value });
  }
  return normalized;
}

function maskClipPath(mask: any, width: number, height: number): string | null {
  if (!mask || mask.inv === true || (mask.mode && mask.mode !== 'a') || mask.pt?.a !== 0) return null;
  const path = mask.pt?.k;
  const vertices = Array.isArray(path?.v) ? path.v : null;
  if (!vertices || vertices.length < 3) return null;
  const inTangents = Array.isArray(path.i) ? path.i : [];
  const outTangents = Array.isArray(path.o) ? path.o : [];
  const hasCurves = [...inTangents, ...outTangents].some((point: unknown) => Array.isArray(point) && point.some((value: unknown) => Number(value) !== 0));
  if (hasCurves) return null;
  const points: Array<string | null> = vertices.map((point: unknown) => {
    const values = numberArray(point);
    if (!values || values.length < 2) return null;
    return `${(values[0] / width) * 100}% ${(values[1] / height) * 100}%`;
  });
  return points.some((point: string | null) => point === null) ? null : `polygon(${points.join(', ')})`;
}

/**
 * Recognises image-only Lottie files with static geometry and opacity keyframes.
 * They can be played by CSS without downloading the general-purpose Lottie runtime.
 */
export function parseRasterLottie(value: unknown): RasterLottieAnimation | null {
  if (!value || typeof value !== 'object') return null;
  const data = value as any;
  const width = Number(data.w);
  const height = Number(data.h);
  const frameRate = Number(data.fr);
  const inFrame = Number(data.ip);
  const outFrame = Number(data.op);
  if (![width, height, frameRate, inFrame, outFrame].every(Number.isFinite) || width <= 0 || height <= 0 || frameRate <= 0 || outFrame <= inFrame) return null;
  if (!Array.isArray(data.assets) || !Array.isArray(data.layers) || data.layers.length === 0) return null;

  const assets = new Map<string, string>();
  for (const asset of data.assets) {
    if (typeof asset?.id !== 'string' || typeof asset?.p !== 'string' || !asset.p.startsWith('data:image/')) continue;
    assets.set(asset.id, asset.p);
  }

  const layers: RasterLottieLayer[] = [];
  for (const [index, layer] of data.layers.entries()) {
    const source = layer?.ty === 2 && typeof layer.refId === 'string' ? assets.get(layer.refId) : null;
    if (!source || Number(layer.ip) !== inFrame || Number(layer.op) !== outFrame) return null;
    if (layer.parent != null || layer.tt != null || layer.tm != null || layer.ef != null || Number(layer.sr ?? 1) !== 1 || Number(layer.st ?? 0) !== 0) return null;
    if (!isStaticValue(layer.ks?.p, [0, 0]) || !isStaticValue(layer.ks?.a, [0, 0]) || !isStaticValue(layer.ks?.s, [100, 100]) || !isStaticValue(layer.ks?.r, 0)) return null;

    const stops = opacityStops(layer.ks?.o, inFrame, outFrame);
    if (!stops) return null;
    let clipPath: string | undefined;
    if (layer.masksProperties != null) {
      if (!Array.isArray(layer.masksProperties) || layer.masksProperties.length !== 1) return null;
      const parsedMask = maskClipPath(layer.masksProperties[0], width, height);
      if (!parsedMask) return null;
      clipPath = parsedMask;
    }
    layers.push({
      src: source,
      clipPath,
      initialOpacity: stops[0].value,
      opacityStops: stops,
      zIndex: data.layers.length - index,
    });
  }

  return {
    width,
    height,
    durationMs: ((outFrame - inFrame) / frameRate) * 1000,
    layers,
  };
}
